import { useNavigate } from "react-router-dom";
import { Sparkles, Heart, BookOpen, MessageCircle, TrendingUp } from "lucide-react";

const LandingPageEssence = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f1e8] via-[#e8dcc8] to-[#d4c4a8]">
      {/* Hero Section */}
      <div className="relative min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-96 h-96 bg-[#d4af37]/10 rounded-full blur-3xl -top-20 -left-20 animate-pulse"></div>
          <div className="absolute w-96 h-96 bg-[#7a9b76]/10 rounded-full blur-3xl -bottom-20 -right-20 animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>

        <div className="relative z-10 text-center max-w-4xl mx-auto fade-in">
          <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 backdrop-blur-sm border border-[#c9a86a]/30">
            <Sparkles className="w-4 h-4 text-[#8b6f47]" />
            <span className="text-sm text-[#8b6f47]">Guía de Automaestría Emocional</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" style={{color: '#8b6f47', textShadow: '0 0 20px rgba(212,175,55,0.2)'}}>
            Tu Viaje hacia la
            <span className="block mt-2 bg-gradient-to-r from-[#d4af37] via-[#c9a86a] to-[#8b6f47] bg-clip-text text-transparent">
              Automaestría Consciente
            </span>
          </h1>

          <p className="text-base sm:text-lg text-[#5a5a5a] mb-12 max-w-2xl mx-auto leading-relaxed">
            Descubre el poder de la IA consciente para guiarte en tu camino de transformación personal. 
            Una experiencia exclusiva para ti.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              data-testid="comenzar-btn"
              onClick={() => navigate("/auth")}
              className="bg-gradient-to-r from-[#d4af37] to-[#c9a86a] text-white text-lg px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all"
            >
              Comenzar Ahora
            </button>
            <button
              data-testid="info-btn"
              onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
              className="bg-transparent text-[#8b6f47] border-2 border-[#c9a86a] text-lg px-8 py-4 rounded-full font-semibold hover:bg-[#d4af37]/10 hover:border-[#d4af37] transition-all"
            >
              Descubre Más
            </button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16 text-[#8b6f47]" style={{textShadow: '0 0 20px rgba(212,175,55,0.2)'}}>
            ¿Qué Encontrarás?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div data-testid="feature-card-lessons" className="card bg-white/70 backdrop-blur-sm rounded-2xl p-8 border border-[#c9a86a]/20 hover:border-[#d4af37]/40 transition-all">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#d4af37] to-[#c9a86a] flex items-center justify-center mb-6">
                <BookOpen className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-[#8b6f47]">Lecciones Profundas</h3>
              <p className="text-[#6a6a6a] leading-relaxed">
                Contenido cuidadosamente diseñado para guiarte en tu desarrollo personal y espiritual.
              </p>
            </div>

            <div data-testid="feature-card-ai" className="card bg-white/70 backdrop-blur-sm rounded-2xl p-8 border border-[#7a9b76]/20 hover:border-[#7a9b76]/40 transition-all">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#7a9b76] to-[#a8c4a4] flex items-center justify-center mb-6">
                <MessageCircle className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-[#7a9b76]">Guía con IA</h3>
              <p className="text-[#6a6a6a] leading-relaxed">
                Chat personalizado con inteligencia artificial entrenada en principios de automaestría.
              </p>
            </div>

            <div data-testid="feature-card-journal" className="card bg-white/70 backdrop-blur-sm rounded-2xl p-8 border border-[#d4a5a5]/20 hover:border-[#d4a5a5]/40 transition-all">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#d4a5a5] to-[#e5c4c4] flex items-center justify-center mb-6">
                <Heart className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-[#d4a5a5]">Diario Personal</h3>
              <p className="text-[#6a6a6a] leading-relaxed">
                Reflexiona diariamente y recibe insights de IA sobre tu viaje interior.
              </p>
            </div>

            <div data-testid="feature-card-exercises" className="card bg-white/70 backdrop-blur-sm rounded-2xl p-8 border border-[#b8a89a]/20 hover:border-[#b8a89a]/40 transition-all">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#b8a89a] to-[#c9baaa] flex items-center justify-center mb-6">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-[#8b6f47]">Ejercicios Prácticos</h3>
              <p className="text-[#6a6a6a] leading-relaxed">
                Prácticas diarias para integrar los aprendizajes en tu vida cotidiana.
              </p>
            </div>

            <div data-testid="feature-card-progress" className="card bg-white/70 backdrop-blur-sm rounded-2xl p-8 border border-[#7a9b76]/20 hover:border-[#7a9b76]/40 transition-all">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#8aab86] to-[#7a9b76] flex items-center justify-center mb-6">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-[#7a9b76]">Seguimiento</h3>
              <p className="text-[#6a6a6a] leading-relaxed">
                Visualiza tu progreso y mantén la motivación en tu camino de crecimiento.
              </p>
            </div>

            <div data-testid="feature-card-natal" className="card bg-white/70 backdrop-blur-sm rounded-2xl p-8 border border-[#d4af37]/20 hover:border-[#d4af37]/40 transition-all">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#d4af37] to-[#e5c158] flex items-center justify-center mb-6">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-[#d4af37]">Carta Natal</h3>
              <p className="text-[#6a6a6a] leading-relaxed">
                Guía personalizada con tu carta natal astrológica para profundizar en tu esencia.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            <span className="bg-gradient-to-r from-[#d4af37] to-[#8b6f47] bg-clip-text text-transparent">
              Comienza tu Transformación Hoy
            </span>
          </h2>
          <p className="text-lg text-[#5a5a5a] mb-8">
            Únete a una comunidad de personas comprometidas con su crecimiento personal.
          </p>
          <button
            data-testid="comenzar-footer-btn"
            onClick={() => navigate("/auth")}
            className="bg-gradient-to-r from-[#d4af37] to-[#c9a86a] text-white text-lg px-10 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all"
          >
            Empezar Gratis
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-[#c9a86a]/30 py-8 px-4 bg-white/40">
        <div className="max-w-6xl mx-auto text-center text-[#8b6f47] text-sm">
          <p>© 2025 ESSENC·IA. Todos los derechos reservados.</p>
          <div className="mt-2 flex items-center justify-center gap-2">
            <Heart className="w-4 h-4 text-[#d4a5a5]" />
            <span>Creado con consciencia y amor</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPageEssence;